<template>
  <v-dialog v-model="showDialog" max-width="500">
    <v-card>
      <v-card-title>Edit Group</v-card-title>
      <v-card-text>
        <v-form ref="editGroupForm" v-model="editGroupValid" @submit.prevent="updateGroup">
          <v-text-field
            v-model="editGroupData.name"
            label="Name"
            :rules="[v => !!v || 'Name is required']"
            required
            @keydown.enter.prevent="updateGroup"
          ></v-text-field>
          <v-textarea
            v-model="editGroupData.description"
            label="Description (optional)"
            rows="2"
            @keydown.enter.prevent="updateGroup"
          ></v-textarea>
          <v-text-field
            v-model="editGroupData.color"
            label="Color (optional)"
            placeholder="#FF5722 or red"
            @keydown.enter.prevent="updateGroup"
          ></v-text-field>
          
          <!-- Current Image Display -->
          <div v-if="editGroupData.image_url" class="mb-4">
            <v-card variant="outlined">
              <v-card-title class="text-subtitle-1 d-flex align-center justify-space-between">
                Current Image
                <v-btn
                  color="error"
                  variant="outlined"
                  size="small"
                  prepend-icon="mdi-delete"
                  @click="removeGroupImage"
                  :disabled="isUpdatingGroup"
                >
                  Remove Image
                </v-btn>
              </v-card-title>
              <v-img
                :src="getAuthenticatedImageUrl(editGroupData.image_url)"
                :alt="`${editGroupData.name} group image`"
                max-height="200"
                max-width="100%"
                contain
                class="mx-2 mb-2"
              >
                <template #error>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-icon>mdi-image-broken-variant</v-icon>
                    <span class="ml-2">Failed to load image</span>
                  </v-row>
                </template>
              </v-img>
            </v-card>
          </div>

          <!-- AI Image Generation -->
          <div>
            <v-checkbox
              v-model="editGroupData.generateImage"
              :label="editGroupData.image_url ? 'Regenerate AI image for this group' : 'Generate AI image for this group'"
              density="compact"
              class="mt-2"
              :hint="editGroupData.image_url ? 'Check to replace the current image with a new one' : 'Check to enable image generation for this group'"
              persistent-hint
            ></v-checkbox>
            
            <div v-if="editGroupData.generateImage">
              <v-textarea
                v-model="editGroupData.imagePrompt"
                label="Image generation prompt"
                rows="3"
                variant="outlined"
                class="mt-2"
                hint="This prompt will be used to generate an AI image for the group"
                persistent-hint
              ></v-textarea>
              
              <v-btn
                color="secondary"
                variant="outlined"
                prepend-icon="mdi-image-plus"
                @click="generateImageForEditGroup"
                :disabled="!editGroupData.imagePrompt.trim() || isUpdatingGroup"
                :loading="isUpdatingGroup"
                class="mt-2"
              >
                {{ editGroupData.image_url ? 'Regenerate Image' : 'Generate Image' }}
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancelEdit" :disabled="isUpdatingGroup">Cancel</v-btn>
        <v-btn 
          ref="updateGroupButton" 
          color="primary" 
          @click="updateGroup" 
          :disabled="!editGroupValid || isUpdatingGroup"
          :loading="isUpdatingGroup"
        >
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useGroupStore } from '../stores/group'
import { useListStore } from '../stores/list'
import { useImageAuthentication } from '../composables/useImageAuthentication'

export default {
  name: 'EditGroupDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    groupData: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue', 'updated', 'message'],
  setup(props, { emit }) {
    const groupStore = useGroupStore()
    const listStore = useListStore()
    
    // Add image authentication composable (for regular authenticated users)
    const { getAuthenticatedImageUrl } = useImageAuthentication(
      ref(false), // not shared
      ref(null),  // no link_id
      ref(null),  // no hmac
      ref(null)   // no authenticated password
    )
    
    const editGroupForm = ref(null)
    const editGroupValid = ref(false)
    const isUpdatingGroup = ref(false)
    const editingGroup = ref(null)
    
    const editGroupData = ref({
      id: null,
      name: '',
      description: '',
      color: '',
      image_url: '',
      generateImage: false,
      imagePrompt: ''
    })
    
    const showDialog = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })
    
    const showMessage = (message, color = 'success') => {
      emit('message', { message, color })
    }
    
    const removeGroupImage = async () => {
      if (!editingGroup.value) return
      
      if (!confirm('Are you sure you want to remove the image from this group?')) return
      
      isUpdatingGroup.value = true
      
      try {
        const groupData = {
          name: editGroupData.value.name,
          description: editGroupData.value.description,
          color: editGroupData.value.color,
          image_url: null // This will remove the image
        }
        
        const result = await groupStore.updateGroup(editingGroup.value.id, groupData)
        if (result.success) {
          // Update the dialog data
          editGroupData.value.image_url = null
          
          // Update the editing group reference
          editingGroup.value.image_url = null
          
          showMessage('Image removed successfully!')
          emit('updated')
        } else {
          showMessage(result.error, 'error')
        }
      } catch (error) {
        showMessage('Error removing image', 'error')
      } finally {
        isUpdatingGroup.value = false
      }
    }
    
    const generateImageForEditGroup = async () => {
      if (!editingGroup.value || !editGroupData.value.imagePrompt.trim()) return
      
      isUpdatingGroup.value = true
      
      try {
        const groupData = {
          generate_image: true,
          image_prompt: editGroupData.value.imagePrompt
        }
        
        const result = await groupStore.updateGroup(editingGroup.value.id, groupData)
        if (result.success && result.group?.image_url) {
          // Update the dialog with the new image
          editGroupData.value.image_url = result.group.image_url
          editGroupData.value.generateImage = false // Reset the checkbox
          
          // Update the editing group reference
          editingGroup.value.image_url = result.group.image_url
          
          showMessage('AI image generated successfully!')
          emit('updated')
          
          // Refresh the current list to show updated group data in background
          if (listStore.currentList) {
            await listStore.setCurrentList(listStore.currentList)
          }
        } else {
          showMessage('Image generation failed', 'error')
        }
      } catch (error) {
        showMessage('Error generating image', 'error')
      } finally {
        isUpdatingGroup.value = false
      }
    }
    
    const updateGroup = async () => {
      if (!editGroupValid.value || !editingGroup.value) return
      
      isUpdatingGroup.value = true
      
      const groupData = {
        name: editGroupData.value.name,
        description: editGroupData.value.description,
        color: editGroupData.value.color
      }
      
      try {
        const result = await groupStore.updateGroup(editingGroup.value.id, groupData)
        if (result.success) {
          showMessage('Group updated successfully!')
          emit('updated')
          cancelEdit()
          
          // Refresh the current list to show updated group data
          if (listStore.currentList) {
            await listStore.setCurrentList(listStore.currentList)
          }
        } else {
          showMessage(result.error, 'error')
        }
      } finally {
        isUpdatingGroup.value = false
      }
    }
    
    const cancelEdit = () => {
      editingGroup.value = null
      editGroupData.value = {
        id: null,
        name: '',
        description: '',
        color: '',
        image_url: '',
        generateImage: false,
        imagePrompt: ''
      }
      showDialog.value = false
    }
    
    // Watch for groupData prop changes to populate the form
    watch(() => props.groupData, (newGroupData) => {
      if (newGroupData) {
        editingGroup.value = newGroupData
        editGroupData.value = {
          id: newGroupData.id,
          name: newGroupData.name,
          description: newGroupData.description || '',
          color: newGroupData.color || '',
          image_url: newGroupData.image_url || '',
          generateImage: false,
          imagePrompt: ''
        }
      }
    }, { immediate: true })
    
    // Watch for edit group generateImage checkbox to set default prompt
    watch(() => editGroupData.value.generateImage, (newVal) => {
      if (newVal && !editGroupData.value.imagePrompt.trim()) {
        const listName = listStore.currentList?.name || 'list'
        const groupName = editGroupData.value.name || 'group'
        editGroupData.value.imagePrompt = `${listName}: ${groupName}`
      }
    })
    
    // Update edit group prompt when group name changes if image generation is enabled
    watch(() => editGroupData.value.name, (newVal) => {
      if (editGroupData.value.generateImage && newVal) {
        const listName = listStore.currentList?.name || 'list'
        editGroupData.value.imagePrompt = `${listName}: ${newVal}`
      }
    })
    
    return {
      showDialog,
      editGroupForm,
      editGroupValid,
      isUpdatingGroup,
      editGroupData,
      getAuthenticatedImageUrl,
      removeGroupImage,
      generateImageForEditGroup,
      updateGroup,
      cancelEdit
    }
  }
}
</script>